import React, { useState } from "react";

/* Comms preferences step.
 *
 *   • Marketing channels — unticked by default. Per Australian Spam Act
 *     2003 we cannot pre-tick promotional opt-ins; consent has to be
 *     express. Channel list comes from the unified
 *     `venue_marketing_options` table — for IGT venues it's synced from
 *     `GET /marketing/options`, for S7000 it's whatever the venue admin
 *     has configured in the client portal.
 *
 *   • AGM acknowledgement — when the venue has `agm_enabled`, a locked
 *     checkbox is rendered above the channel list with the venue's
 *     custom copy. Pre-checked and disabled — the member has to
 *     acknowledge but can't opt out. Stored separately from marketing
 *     prefs because it's consent, not a preference (mirrors the T&C
 *     audit pattern).
 *
 * Returns on submit:
 *   { marketingOptionIds: number[],      // venue_marketing_options.id
 *     agmAcknowledged: boolean,
 *     agmVersion:      string|null }
 */
export default function StepCommsPrefs({
  venue,
  marketingOptions,
  initialPrefs,
  onSubmit,
  onBack,
}) {
  const agmEnabled = !!venue?.agm_enabled;
  const agmCopy    = (venue?.agm_copy || "").trim();

  // Marketing checkbox state keyed by venue_marketing_options.id.
  // Renewal pre-fill flows through `initialPrefs.marketingOptionIds`.
  const [selected, setSelected] = useState(() => {
    const init = {};
    const preset = new Set(initialPrefs?.marketingOptionIds || []);
    for (const opt of marketingOptions || []) {
      init[opt.id] = preset.has(opt.id);
    }
    return init;
  });

  // AGM defaults to checked when the block is rendered (member can't
  // opt out — the box is disabled). Carrying the state explicitly lets
  // us validate the venue actually opened the page rather than
  // assuming acknowledgement.
  const [agmAck, setAgmAck] = useState(true);
  const [error, setError]   = useState("");

  const toggle = (id) => {
    setSelected((p) => ({ ...p, [id]: !p[id] }));
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (agmEnabled && !agmAck) {
      setError("Please acknowledge the AGM notice to continue.");
      return;
    }
    const marketingOptionIds = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => Number(k))
      .filter((n) => Number.isFinite(n));
    onSubmit({
      marketingOptionIds,
      agmAcknowledged: agmEnabled ? agmAck : false,
      agmVersion:      agmEnabled ? venue?.agm_version || null : null,
    });
  };

  return (
    <>
      <h2 className="step-title">Communications</h2>
      <p className="step-sub">
        Let us know how you'd like to hear from us. You can change these
        preferences any time after joining.
      </p>

      {error && <div className="su-alert error">⚠ {error}</div>}

      <form onSubmit={handleSubmit}>
        {agmEnabled && (
          <>
            <div className="su-divider">Notices</div>
            <div
              className="su-terms-body"
              style={{
                maxHeight: "none",
                padding: "0.9rem 1rem",
                marginBottom: "0.75rem",
              }}
            >
              {agmCopy
                ? agmCopy.split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)
                : <p>
                    By joining, I acknowledge that I will receive Annual
                    General Meeting notices from the venue. This
                    notification cannot be disabled.
                  </p>}
            </div>
            <div className="su-field" style={{ marginBottom: "1.5rem" }}>
              <label className="su-checkbox-row">
                <input
                  type="checkbox"
                  checked={agmAck}
                  disabled
                  readOnly
                />
                <span>
                  I acknowledge the AGM notice above
                  {venue?.agm_version ? ` (${venue.agm_version})` : ""}.
                </span>
              </label>
            </div>
          </>
        )}

        {marketingOptions?.length > 0 && (
          <>
            <div className="su-divider">Communication preferences</div>
            <p
              className="step-sub"
              style={{ marginTop: 0, marginBottom: "0.75rem" }}
            >
              Tick the channels you're happy for us to use for offers,
              events, and news. All optional.
            </p>
            <div className="su-field">
              {marketingOptions.map((opt) => (
                <label
                  key={opt.id}
                  className="su-checkbox-row"
                  style={{ marginBottom: "0.5rem" }}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[opt.id]}
                    onChange={() => toggle(opt.id)}
                  />
                  <span>{opt.name}</span>
                </label>
              ))}
            </div>
          </>
        )}

        <div className="su-btn-row">
          <button type="button" className="su-btn su-btn-outline" onClick={onBack}>
            Back
          </button>
          <button type="submit" className="su-btn su-btn-primary">
            Continue
          </button>
        </div>
      </form>
    </>
  );
}
